// ============= CRITICAL: Import fetch tracing FIRST =============
// This MUST be the first import to patch window.fetch before any other code runs
import './lib/fetchTrace';

import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from "@sentry/react";
import { AuthProvider } from './hooks/useAuth'
import { PlayerAuthProvider } from './hooks/usePlayerAuth'
import { initPerfFromUrl } from './lib/perf/supabaseCallLog'
import { scrubSentryEvent, scrubBreadcrumbData } from './lib/sentryScrub'
import App from './App.tsx'
import './index.css'

const rootElement = document.getElementById("root")!;

// Prevent browser/extension auto-translation from mutating React-managed DOM.
// Chrome Translate can otherwise cause NotFoundError insertBefore/removeChild crashes
// during module transitions where text nodes are conditionally re-rendered.
try {
  document.documentElement.setAttribute('translate', 'no');
  document.documentElement.classList.add('notranslate');
  document.body.setAttribute('translate', 'no');
  document.body.classList.add('notranslate');
  rootElement.setAttribute('translate', 'no');
  rootElement.classList.add('notranslate');

  if (!document.querySelector('meta[name="google"][content="notranslate"]')) {
    const meta = document.createElement('meta');
    meta.name = 'google';
    meta.content = 'notranslate';
    document.head.appendChild(meta);
  }
} catch (_) {
  // Ignore DOM access errors in restricted environments.
}

try {
  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
} catch (_) {
  // Keep normal DOM behavior if prototype patching is blocked.
}

// Initialize perf logging from URL param (?perf=1)
initPerfFromUrl();

// Clear chunk-reload flag on successful app load (enables future auto-reloads)
try {
  sessionStorage.removeItem('chunk-reload');
} catch (_) {
  // Ignore sessionStorage errors
}

// Initialize Sentry when DSN is configured (Q06: replay masking + secret scrub)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_APP_ENV || "production",
    beforeSend(event) {
      return scrubSentryEvent(event);
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data && typeof breadcrumb.data === 'object') {
        breadcrumb.data = scrubBreadcrumbData(breadcrumb.data as Record<string, unknown>);
      }
      return breadcrumb;
    },
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Sentry error boundary fallback component
const SentryFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background p-4">
    <div className="text-center space-y-4">
      <h1 className="text-2xl font-bold text-foreground">Jotain meni pieleen</h1>
      <p className="text-muted-foreground">Yritä ladata sivu uudelleen.</p>
      <button 
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
      >
        Lataa uudelleen
      </button>
    </div>
  </div>
);

createRoot(rootElement).render(
  <Sentry.ErrorBoundary fallback={<SentryFallback />}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PlayerAuthProvider>
          <App />
        </PlayerAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);
